import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { Sun, Moon, ArrowLeft, Mail, MapPin, Phone, Clock, Send, MessageSquare, Headphones, FileText } from 'lucide-react';
import { MetaTradifyLogo, MetaTradifyLogoSmall } from '@/components/MetaTradifyLogo';
import { useState } from 'react';
import { toast } from 'sonner';

export default function ContactUsPage() {
  const { theme, toggleTheme } = useTheme();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Please fill in all required fields'); return; }
    setSending(true);
    setTimeout(() => {
      toast.success('Message sent! We will get back to you within 24 hours.');
      setForm({ name: '', email: '', subject: '', message: '' });
      setSending(false);
    }, 1200);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 glass border-b border-[var(--border-subtle)]" data-testid="contact-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <MetaTradifyLogo size={32} />
            <span className="text-lg font-bold font-['Outfit'] tracking-tight">metaTradify</span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-lg bg-[var(--hover-bg)] border border-[var(--border-subtle)] transition-all">
              {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-green-500" />}
            </button>
            <Link to="/" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="max-w-2xl mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--border-subtle)] bg-[var(--hover-bg)] text-xs text-[var(--text-secondary)] mb-6">
              <MessageSquare className="w-3 h-3 text-green-400" /> Get in touch
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] tracking-tight leading-[1.1] mb-4" data-testid="contact-heading">
              Contact <span className="text-green-400">Us</span>
            </h1>
            <p className="text-base text-[var(--text-secondary)] max-w-lg">
              Have a question, feedback, or need support? Our team is here to help. Reach out and we'll respond within 24 hours.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Contact Form */}
            <div className="lg:col-span-7">
              <form onSubmit={handleSubmit} className="p-8 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg space-y-5" data-testid="contact-form">
                <h2 className="text-lg font-semibold font-['Outfit'] mb-1">Send us a message</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs text-[var(--text-secondary)] font-medium">Name *</label>
                    <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="contact-name-input" placeholder="Your name"
                      className="w-full px-4 py-2.5 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-green-500 transition-colors" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-[var(--text-secondary)] font-medium">Email *</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="contact-email-input" placeholder="you@example.com"
                      className="w-full px-4 py-2.5 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-green-500 transition-colors" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[var(--text-secondary)] font-medium">Subject</label>
                  <input type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    data-testid="contact-subject-input" placeholder="How can we help?"
                    className="w-full px-4 py-2.5 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-green-500 transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[var(--text-secondary)] font-medium">Message *</label>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-message-input" placeholder="Tell us more about your inquiry..." rows={5}
                    className="w-full px-4 py-2.5 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-green-500 transition-colors resize-none" />
                </div>
                <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all hover:shadow-lg hover:shadow-green-500/25 disabled:opacity-50">
                  <Send className="w-4 h-4" /> {sending ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-5 space-y-5">
              {[
                { icon: Mail, label: 'Email', value: 'support@metatradify.com', sub: 'We reply within 24 hours' },
                { icon: Phone, label: 'Phone', value: '+91 1800-123-4567', sub: 'Mon-Fri, 9 AM - 6 PM IST' },
                { icon: MapPin, label: 'Office', value: 'Cyber City, Bangalore', sub: 'Karnataka, India 560001' },
                { icon: Clock, label: 'Business Hours', value: '9:00 AM - 6:00 PM IST', sub: 'Monday to Friday' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex gap-4 p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg hover:border-green-500/20 transition-colors" data-testid={`contact-info-${item.label.toLowerCase().replace(' ', '-')}`}>
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-secondary)] mb-0.5">{item.label}</p>
                      <p className="text-sm font-medium text-[var(--text-primary)]">{item.value}</p>
                      <p className="text-xs text-[var(--text-tertiary)] mt-0.5">{item.sub}</p>
                    </div>
                  </div>
                );
              })}

              {/* Quick Links */}
              <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
                <h3 className="text-sm font-semibold font-['Outfit'] mb-3">Quick Links</h3>
                <div className="space-y-2">
                  {[
                    { icon: Headphones, label: 'Help Center & FAQ', path: '/tickets' },
                    { icon: FileText, label: 'Privacy Policy', path: '/privacy-policy' },
                    { icon: FileText, label: 'About Us', path: '/about' },
                  ].map((link, i) => {
                    const Icon = link.icon;
                    return (
                      <Link key={i} to={link.path} className="flex items-center gap-2.5 px-3 py-2 rounded text-sm text-[var(--text-secondary)] hover:text-green-400 hover:bg-green-500/5 transition-colors">
                        <Icon className="w-4 h-4" /> {link.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[var(--border-subtle)] py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <MetaTradifyLogoSmall size={24} />
            <span className="text-sm font-semibold font-['Outfit']">metaTradify</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-[var(--text-tertiary)]">
            <Link to="/about" className="hover:text-[var(--text-secondary)] transition-colors">About</Link>
            <Link to="/contact" className="hover:text-[var(--text-secondary)] transition-colors">Contact</Link>
            <Link to="/privacy-policy" className="hover:text-[var(--text-secondary)] transition-colors">Privacy</Link>
          </div>
          <p className="text-xs text-[var(--text-tertiary)]">&copy; 2025 metaTradify. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
